import React, { useEffect, useMemo, useRef, useState } from 'react'
import { secureApi } from '../../Config/api'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Datepicker, DeviceDrawer, RouteHistoryMap, VehicleMap } from '../../Components'
import { MdLogin, MdLogout, MdPersonOutline } from "react-icons/md"
import { FaChevronDown, FaChevronUp } from 'react-icons/fa'
import { dateFormate, translations, pageSizeOptions, tableDateFormate } from '../../utils/constants'
import { Button, Empty, message, Pagination, Skeleton, Table } from 'antd'
import { GET, POST } from '../../utils/apis'
import io from 'socket.io-client'
import { verifyToken } from '../../utils/helpers'
import { setDeviceInfoRedux } from '../../Redux/actions/deviceInfoAction'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Download, ExternalLink } from 'lucide-react'
import { routeForecastUrl } from '../../utils/routeForecastLink'
import { throttle } from 'lodash'
import { elmecLogo } from '../../assets'
import moment from 'moment'

const VehiclesDetails = ({ language }) => {
    const location = useLocation()
    const dispatch = useDispatch()
    const history = useHistory()
    const { vehicleId } = useParams()
    const vehicle = location.state?.vehicle
    const user = useSelector(state => state?.authReducer?.user)
    const organizationDetails = useSelector(state => state?.authReducer?.organization)
    const [isDrawerVisible, setIsDrawerVisible] = useState(false)
    const [selectedDevice, setSelectedDevice] = useState(null)
    const [organizationId, setOrganizationId] = useState(organizationDetails?.id)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [peopleCounter, setPeopleCounter] = useState([])
    const [vehicleDetails, setVehicleDetails] = useState({})
    const [totalSummary, setTotalSummary] = useState(null)
    // The vehicle's own devices, flattened from the grouped JSON the
    // details call already returns — no need to pull every device in the
    // organization just to filter it down to this vehicle.
    const devices = useMemo(() => {
        const grouped = vehicleDetails?.devices || {}
        return Object.entries(grouped).flatMap(([deviceType, list]) =>
            (Array.isArray(list) ? list : []).map((d) => ({
                ...d,
                device_type: d?.device_type || deviceType,
                vehicle_id: d?.vehicle_id || vehicleDetails?.id,
                vehicle_name: vehicleDetails?.vehicle_name,
            }))
        )
    }, [vehicleDetails])
    const routerSerial = vehicleDetails?.devices?.router?.[0]?.serial_number || null
    // Device the public forecast page is opened with: display ID first, serial as fallback.
    const forecastDevice = devices.find((d) => d?.ud_sn)?.ud_sn || routerSerial || devices[0]?.serial_number || null
    // Socket callbacks are registered once per day-selection; they read the
    // latest fetchers through refs so they never call a stale closure.
    const fetchDeviceInfoRef = useRef(() => {})
    const fetchVehicleDetailsRef = useRef(() => {})
    // The device-info call returns the raw GPS trace first (snapped=false)
    // while the road-snapped version is computed server-side; poll a few
    // times so the smooth line replaces it without a manual refresh.
    const snapRetryRef = useRef({ key: null, count: 0, timer: null })
    useEffect(() => () => clearTimeout(snapRetryRef.current.timer), [])
    const [deviceInfo, setDeviceInfo] = useState(null)
    const [selectedDate, setSelectedDate] = useState(new Date())
    const [isLoading, setIsLoading] = useState(true)
    const [routeHistory, setRouteHistory] = useState([])
    const [routeHistoryLoading, setRouteHistoryLoading] = useState(false)
    const [expandedJourney, setExpandedJourney] = useState(null)
    // Route History is paged client-side: the endpoint already returns only
    // the selected day's journeys, so the list is small enough to page here.
    const ROUTE_HISTORY_PAGE_SIZE = 5
    const [routeHistoryPage, setRouteHistoryPage] = useState(1)
    const routeHistoryPages = Math.max(1, Math.ceil(routeHistory.length / ROUTE_HISTORY_PAGE_SIZE))
    const routeHistoryCurrent = Math.min(routeHistoryPage, routeHistoryPages)
    const pagedRouteHistory = routeHistory.slice(
        (routeHistoryCurrent - 1) * ROUTE_HISTORY_PAGE_SIZE,
        routeHistoryCurrent * ROUTE_HISTORY_PAGE_SIZE
    )

    // Daily route history — refetches when the vehicle or the picked day
    // changes.
    useEffect(() => {
        const id = vehicle?.id || vehicleId
        if (!id || !selectedDate) return
        let cancelled = false
        let retryTimer = null
        let retries = 0
        // silent: the server answered quickly with raw traces and is still
        // road-matching them in the background — poll a few more times
        // without flashing the skeleton so the red lines sharpen in place.
        const fetchHistory = async (silent = false) => {
            if (!silent) {
                setRouteHistoryLoading(true)
                setExpandedJourney(null)
                setRouteHistoryPage(1)
            }
            try {
                const response = await secureApi.get(GET?.GET_VEHICLE_ROUTE_HISTORY, {
                    params: { vehicle_id: id, date: moment(selectedDate).format('YYYY-MM-DD') },
                })
                if (cancelled) return
                if (response?.success) {
                    setRouteHistory(response?.data?.journeys || [])
                    if (response?.data?.normalizing && retries < 6) {
                        retries += 1
                        retryTimer = setTimeout(() => fetchHistory(true), 5000)
                    }
                } else if (!silent) {
                    setRouteHistory([])
                }
            } catch (error) {
                if (!silent && !cancelled) setRouteHistory([])
            } finally {
                if (!silent && !cancelled) setRouteHistoryLoading(false)
            }
        }
        fetchHistory()
        return () => {
            cancelled = true
            clearTimeout(retryTimer)
        }
    }, [vehicleId, vehicle?.id, selectedDate])

    useEffect(() => {
        verifyToken(history, dispatch)
    }, [])

    // Details + people counter: once on mount and whenever the day changes.
    useEffect(() => {
        if (selectedDate !== undefined) {
            fetchVehicleDetails()
        }
    }, [selectedDate])

    // Device position/trace: only once the router's serial is known, and
    // again when the day changes. (Previously four effects each fired this,
    // so the slow map-matching endpoint was hit 3-4 times per page load.)
    useEffect(() => {
        if (!routerSerial) return
        fetchDeviceInfo()
    }, [routerSerial, selectedDate])

    useEffect(() => {
        const isToday = moment(selectedDate).isSame(new Date(), 'day')
        const throttledFetch = throttle(() => fetchDeviceInfoRef.current(), 5000)
        const throttledFetchPeopleCounter = throttle(() => fetchVehicleDetailsRef.current(), 5000)

        let socket

        // console.log('selectedDate', selectedDate, isToday)

        if (isToday) {
            socket = io()
            socket.on('connect', () => console.log('Socket connected'))
            socket.on('deviceinfo', throttledFetch)
            socket.on('peopleCounterUpdated', throttledFetchPeopleCounter)
        }

        return () => {
            if (socket) {
                socket.off('deviceinfo', throttledFetch)
                socket.off('peopleCounterUpdated', throttledFetchPeopleCounter)
                console.log('socket disconnectedo')
                socket.disconnect()
            }
        }
    }, [selectedDate])

    const isTodaySelected = () => moment(selectedDate).isSame(new Date(), 'day')

    const fetchDeviceInfo = async () => {
        const serialNumber = vehicleDetails?.devices?.router?.[0]?.serial_number || null

        setIsLoading(!isTodaySelected())
        try {
            if (!serialNumber) {
                setIsLoading(false)
                return
            }
            let formattedDate = null;
            if (selectedDate) {
                const m = moment(selectedDate)
                if (m.isValid()) {
                    formattedDate = m.format(tableDateFormate);
                }
                else {
                    // console.error("Selected date is invalid, skipping date param.");
                }
            }
            const response = await secureApi.get(GET?.GET_DEVICE_INFO, {
                params: { serialNumber, formattedDate },
            })
            // console.log('device info response', response)
            if (response?.success === true) {
                setDeviceInfo(response?.data)
                dispatch(setDeviceInfoRedux(response?.data))

                const snapKey = `${serialNumber}:${formattedDate || 'today'}`
                if (snapRetryRef.current.key !== snapKey) {
                    clearTimeout(snapRetryRef.current.timer)
                    snapRetryRef.current = { key: snapKey, count: 0, timer: null }
                }
                if (response?.data?.snapped === false && snapRetryRef.current.count < 5) {
                    clearTimeout(snapRetryRef.current.timer)
                    snapRetryRef.current.count += 1
                    snapRetryRef.current.timer = setTimeout(() => fetchDeviceInfoRef.current(), 4000)
                }
            }
            else if (response?.success === false) {
                setDeviceInfo(null)
                // message.error(response.data?.message)
            }
            setIsLoading(false)
        }

        catch (error) {
            setIsLoading(false)
            // console.error("Error fetching device info:", error)
            // message.error("Error fetching device info. Please try again.")
        }
    }

    const handleDateChange = (date) => {
        setSelectedDate(date)
    }

    const fetchVehicleDetails = async () => {
        try {
            // Send the picked day as a plain calendar date: a Date object
            // serialises in UTC and, east of Greenwich, lands on the previous
            // day server-side.
            const response = await secureApi.post(POST?.VEHICLE_DETAILS, {
                vehicleId: vehicle?.id,
                selectedDate: moment(selectedDate).format('YYYY-MM-DD'),
            })
            if (response?.success) {
                setVehicleDetails(response?.data?.vehicleDetails || {})
                setPeopleCounter(response?.data?.records || [])
                setTotalSummary(response?.data?.totalSummary)
            }
            else if (response?.success === false) {
                message.error(response?.error || response?.message)
            }
        }
        catch (err) {
            // console.error('Error fetching vehicle details:', err)
            setError('Error fetching vehicle details.')
        }
        finally {
            setLoading(false)
        }
    }

    fetchDeviceInfoRef.current = fetchDeviceInfo
    fetchVehicleDetailsRef.current = fetchVehicleDetails

    const filteredData = peopleCounter?.filter((record) => (record?.exit !== null || record?.enter !== null) && record?.mac_address !== null || record?.logged_time !== null)?.map((record, index) => ({
        key: index,
        vehicleName: vehicle?.vehicle_name,
        macAddress: record?.mac_address || null,
        totalEnter: record?.enter ?? 'Empty',
        totalPassengers: record?.total_passengers ?? 'Empty',
        totalExit: record?.exit ?? 'Empty',
        loggedTime: record?.logged_time,
        remainingPeople: record?.remaining_people ?? 'Empty',
        created_at: record?.created_at
    })) || []

    const columns = [
        {
            title: translations[language]?.datetxt,
            dataIndex: 'loggedTime',
            key: 'loggedTime',
            render: (text) => text
                ? new Date(text).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
                : ''
        },
        ...(peopleCounter?.some(vehicle => vehicle.logged_time !== null) ? [
            {
                title: translations[language]?.vehicleName || 'Vehicle Name',
                dataIndex: "vehicleName",
                key: "vehicleName",
            },
        ] : []),
        {
            title: translations[language]?.macAddress || 'MAC Address',
            dataIndex: 'macAddress',
            key: 'macAddress',
            render: (text) => (text ? <span className='vl-plate'>{text}</span> : <span className='vl-muted'>-</span>),
        },

        {
            title: translations[language]?.txtEnterPassengers,
            dataIndex: 'totalEnter',
            key: 'totalEnter',
        },
        {
            title: translations[language]?.txtExitPassengers,
            dataIndex: 'totalExit',
            key: 'totalExit',
        },
        {
            title: translations[language]?.txtTotalPassengers,
            key: 'calculatedRemaining',
            // People on board after this reading: the running enter−exit
            // balance over the day (computed server-side in time order), not
            // this single row's enter−exit which is almost always 0.
            render: (_, record) => {
                const onboard = Number(record.remainingPeople)
                return Number.isFinite(onboard) ? Math.max(0, onboard) : 0
            }
        },
    ]

    const deviceTypes = [
        {
            label: translations[language]?.deviceTypeRouter,
            type: 'router'
        },
        {
            label: translations[language]?.deviceTypePassengerCounters,
            type: 'passenger-counters'
        },
        {
            label: translations[language]?.deviceTypeValidators,
            type: 'validators'
        },
        {
            label: translations[language]?.deviceTypeLcdDisplays,
            type: 'lcd-displays'
        },
        {
            label: translations[language]?.deviceTypeDvr,
            type: 'dvr'
        },
        {
            label: translations[language]?.deviceTypeXpad,
            type: 'xpad'
        },
    ]

    const handleDeviceClick = (deviceType) => {
        const matchedDevices = devices?.filter(d => d.vehicle_id === vehicle?.id && d?.device_type === deviceType)
        if (matchedDevices?.length > 0) {
            setSelectedDevice(deviceType)
            setIsDrawerVisible(true)
        }
    }

    const deviceTypeSelect = devices?.find(d => d.vehicle_id === vehicle?.id && d.device_type === 'router')?.device_type

    const downloadPdf = async () => {
        try {
            // console.log("peopleCounter", peopleCounter)
            const payload = {
                input: peopleCounter?.map((record) => ({
                    time: record?.logged_time
                        ? new Date(record?.logged_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
                        : '-',
                    vehicleName: vehicle?.vehicle_name,
                    enter: record?.enter ?? 0,
                    exit: record?.exit ?? 0,
                    total: Math.max(0, Number(record?.remaining_people) || 0)
                })),
                organizationDetails,
                elmecLogoImage: organizationDetails?.logo || elmecLogo,
                language,
            }

            const response = await secureApi?.post(POST?.DOWNLOAD_VEHICLE_PASEENGER_COUNTER_PDF, payload)
            if (response?.success) {
                const link = document.createElement('a')
                link.href = response?.data
                link.download = `Vehicle_Passenger_Counter_Report_${vehicle?.vehicle_name || 'vehicle'}.pdf`
                link.click()
                message.success("PDF generated successfully!")
            } else {
                message.error(translations[language]?.noDataFound || 'No data Found')
            }
        } catch (error) {
            message.error(error)
        }
    }


    // Vehicle information card rows — one label/value pair each.
    const infoFields = vehicle
        ? [
            { label: translations[language]?.licensePlateLabel, value: vehicle?.license_plate },
            { label: translations[language]?.vehicleModelLabel, value: vehicle?.vehicle_model },
            { label: translations[language]?.yearOfManufactureLabel, value: vehicle?.year_of_manufacture },
            { label: translations[language]?.vehicleCategoryLabel, value: vehicle?.vehicle_category },
            {
                label: translations[language]?.operationStartDateLabel,
                value: vehicle?.operation_start_date
                    ? new Date(vehicle?.operation_start_date).toLocaleDateString('en-GB')
                    : null,
            },
            {
                label: translations[language]?.techMaintenanceNotesLabel,
                value:
                    !vehicle?.tech_maintenance_notes || vehicle?.tech_maintenance_notes === 'undefined'
                        ? null
                        : vehicle?.tech_maintenance_notes,
            },
        ]
        : []

    const statCards = [
        {
            key: 'enter',
            value: totalSummary?.total_entries || 0,
            label: translations[language]?.totalEnter,
            color: '#00E096',
            icon: <MdLogin size={22} color='white' />,
        },
        {
            key: 'exit',
            value: totalSummary?.total_exits || 0,
            label: translations[language]?.totalExit,
            color: '#1976D2',
            icon: <MdLogout size={22} color='white' />,
        },
        {
            key: 'passengers',
            value: totalSummary?.total_remaining || 0,
            label: translations[language]?.totalPassengers,
            color: '#B282D6',
            icon: <MdPersonOutline size={22} color='white' />,
        },
    ]

    return (
        <div className='main-container'>
            <div className='main-inner-container vd-page'>
                <div className='vd-header'>
                    <div className='vd-header-left'>
                        <button
                            type='button'
                            className='vd-back-btn'
                            onClick={() => history.goBack()}
                            aria-label='Back'
                        >
                            <ArrowLeftOutlined />
                        </button>
                        <div>
                            <h1 className='main-heading margin-bottom-0'>
                                {translations[language]?.vehicleDetailsHeading}
                            </h1>
                            {vehicle ? (
                                <div className='vd-subtitle'>
                                    <span className='vd-subtitle-name'>
                                        {vehicle?.vehicle_name || vehicle?.registering_number || '-'}
                                    </span>
                                    {vehicle?.license_plate ? (
                                        <span className='vd-plate'>{vehicle.license_plate}</span>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className='vd-header-right'>
                        {forecastDevice ? (
                            <a
                                className='vd-forecast-link'
                                href={routeForecastUrl(forecastDevice)}
                                target='_blank'
                                rel='noopener noreferrer'
                                title={translations[language]?.openRouteForecast}
                            >
                                <ExternalLink size={16} />
                                <span>{translations[language]?.routeForecastLink}</span>
                            </a>
                        ) : null}
                        <Datepicker
                            format={dateFormate}
                            value={selectedDate}
                            language={language}
                            onChange={handleDateChange}
                        />
                    </div>
                </div>

                {vehicle ? (
                    <div className='vd-card'>
                        <div className='vd-card-header'>
                            <h2 className='vd-card-title'>{translations[language]?.vehicleInformation}</h2>
                        </div>
                        <div className='vd-card-body'>
                            <div className='vd-info-grid'>
                                {infoFields.map((field) => (
                                    <div className='vd-info-item' key={field.label}>
                                        <span className='vd-info-label'>{field.label}</span>
                                        <span className='vd-info-value'>{field.value || '-'}</span>
                                    </div>
                                ))}
                            </div>
                            <div className='vd-devices'>
                                <span className='vd-info-label'>{translations[language]?.attachedDevices}</span>
                                <div className='vd-device-chips'>
                                    {deviceTypes?.map((device) => {
                                        const filteredDevices = devices.filter(
                                            (d) => d.vehicle_id === vehicle?.id && d.device_type === device.type
                                        )
                                        const hasDevices = filteredDevices.length > 0
                                        const primary = filteredDevices[0]
                                        return (
                                            <button
                                                type='button'
                                                key={device.type}
                                                className={`vd-device-chip${hasDevices ? ' vd-device-chip-active' : ''}`}
                                                disabled={!hasDevices}
                                                onClick={() => hasDevices && handleDeviceClick(device.type)}
                                                title={hasDevices ? device.label : undefined}
                                            >
                                                <span className='vd-device-chip-label'>{device.label}</span>
                                                <span className='vd-device-chip-value'>
                                                    {hasDevices
                                                        ? primary?.serial_number || primary?.mac_address || 'N/A'
                                                        : '-'}
                                                </span>
                                                {filteredDevices.length > 1 && (
                                                    <span className='vd-device-chip-badge'>+{filteredDevices.length - 1}</span>
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className='vd-card'>
                        <div className='vd-card-body vd-empty'>
                            {translations[language]?.noVehicleDataAvailable || 'No vehicle data available'}
                        </div>
                    </div>
                )}

                <div className='vd-stats'>
                    {statCards.map((card) => (
                        <div className='vd-stat' key={card.key}>
                            <div className='vd-stat-icon' style={{ backgroundColor: card.color }}>
                                {card.icon}
                            </div>
                            <div>
                                <p className='vd-stat-value'>{card.value}</p>
                                <p className='vd-stat-label'>{card.label}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className='vd-card'>
                    <div className='vd-card-header'>
                        <h2 className='vd-card-title'>{translations[language]?.passengerCounterTitle}</h2>
                        <Button
                            type='primary'
                            className='add-button'
                            onClick={downloadPdf}
                            disabled={!filteredData || filteredData.length === 0}
                        >
                            {translations[language]?.getReport}
                            <Download className='icons' style={{ height: '18px' }} />
                        </Button>
                    </div>
                    <div className='vd-card-body'>
                        <Table
                            dataSource={filteredData}
                            columns={columns}
                            pagination={{
                                showSizeChanger: true,
                                showQuickJumper: true,
                                pageSizeOptions,
                                defaultPageSize: 10
                            }}
                            scroll={{ x: 300 }}
                            locale={{ emptyText: <Empty description={translations[language]?.noDataAvailable || 'No Data Available'} /> }}
                        />
                    </div>
                </div>

                {deviceTypeSelect === 'router' && vehicleDetails?.id ? (
                    <div className='vd-card'>
                        <div className='vd-card-header'>
                            <h2 className='vd-card-title'>
                                {isTodaySelected()
                                    ? translations[language]?.liveLocation
                                    : translations[language]?.deviceLocation}
                            </h2>
                            <span className='vd-card-meta'>{moment(selectedDate).format('DD/MM/YYYY')}</span>
                        </div>
                        <div className='vd-card-body'>
                            <div className='vd-map'>
                                <VehicleMap
                                    deviceInfo={deviceInfo}
                                    vehicleDetails={vehicleDetails}
                                    language={language}
                                    selectedDate={selectedDate}
                                    isLoading={isLoading}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className='vd-card'>
                    <div className='vd-card-header'>
                        <h2 className='vd-card-title'>{translations[language]?.routeHistory}</h2>
                        <span className='vd-card-meta'>{moment(selectedDate).format('DD/MM/YYYY')}</span>
                    </div>
                    <div className='vd-card-body'>
                        {routeHistoryLoading ? (
                            <Skeleton active paragraph={{ rows: 5 }} />
                        ) : routeHistory.length === 0 ? (
                            <div style={{ color: '#888', padding: '12px 0' }}>
                                {translations[language]?.noRoutesFound}
                            </div>
                        ) : (
                            <>
                            {pagedRouteHistory.map((journey, pageIndex) => {
                                // Key on the absolute position so the open
                                // card stays open across page changes.
                                const index = (routeHistoryCurrent - 1) * ROUTE_HISTORY_PAGE_SIZE + pageIndex
                                const statusMeta = {
                                    normal: { label: translations[language]?.normal, color: '#52c41a', bg: '#f6ffed' },
                                    abnormal: { label: translations[language]?.abnormal, color: '#ff4d4f', bg: '#fff1f0' },
                                    no_schedule: { label: translations[language]?.noSchedule, color: '#8c8c8c', bg: '#fafafa' },
                                }[journey?.status] || {}
                                const expanded = expandedJourney === index
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            border: '1px solid #f0f0f0',
                                            borderLeft: `4px solid ${statusMeta?.color || '#ED2328'}`,
                                            borderRadius: 6,
                                            marginBottom: 12,
                                            padding: '10px 12px',
                                        }}
                                    >
                                        <div
                                            onClick={() => setExpandedJourney(expanded ? null : index)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                cursor: 'pointer',
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            {journey?.trip?.line ? (
                                                <span
                                                    style={{
                                                        background: '#ED2328',
                                                        color: '#fff',
                                                        borderRadius: 4,
                                                        padding: '2px 10px',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {journey.trip.line}
                                                </span>
                                            ) : null}
                                            <span style={{ fontWeight: 700 }}>
                                                {journey?.start_time ? moment(journey.start_time).format('HH:mm') : '-'}
                                                {' - '}
                                                {journey?.end_time ? moment(journey.end_time).format('HH:mm') : '-'}
                                            </span>
                                            <span style={{ color: '#888', fontSize: 13, flex: 1 }}>
                                                {journey?.trip?.headsign || ''}
                                                {journey?.trip?.starts
                                                    ? ` (${journey.trip.starts} - ${journey.trip.ends})`
                                                    : ''}
                                            </span>
                                            <span
                                                style={{
                                                    background: statusMeta?.bg,
                                                    color: statusMeta?.color,
                                                    borderRadius: 4,
                                                    padding: '2px 10px',
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                }}
                                            >
                                                {statusMeta?.label}
                                            </span>
                                            {expanded ? (
                                                <FaChevronUp style={{ color: '#888', fontSize: 11 }} />
                                            ) : (
                                                <FaChevronDown style={{ color: '#888', fontSize: 11 }} />
                                            )}
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: 24,
                                                flexWrap: 'wrap',
                                                marginTop: 8,
                                                fontSize: 13,
                                            }}
                                        >
                                            <span>
                                                <strong>{translations[language]?.kmTraveled}:</strong>{' '}
                                                {journey?.km_traveled ?? '-'} km
                                                {journey?.scheduled_km != null && (
                                                    <span style={{ color: '#888' }}>
                                                        {' vs '}{journey.scheduled_km} km ({translations[language]?.scheduledDistance?.toLowerCase()})
                                                    </span>
                                                )}
                                            </span>
                                            <span>
                                                <strong>{translations[language]?.avgSpeed}:</strong>{' '}
                                                {journey?.avg_speed_kmh ?? '-'} km/h
                                            </span>
                                            <span>
                                                <strong>{translations[language]?.maxSpeed}:</strong>{' '}
                                                {journey?.max_speed_kmh ?? '-'} km/h
                                            </span>
                                            {journey?.adherence != null && (
                                                <span>
                                                    <strong>{translations[language]?.adherence}:</strong>{' '}
                                                    {Math.round(journey.adherence * 100)}%
                                                </span>
                                            )}
                                            <span>
                                                <strong>{translations[language]?.totalPassengers}:</strong>{' '}
                                                {journey?.passengers ?? 0}
                                            </span>
                                        </div>
                                        {expanded && (
                                            <div style={{ marginTop: 10 }}>
                                                <RouteHistoryMap
                                                    trace={
                                                        journey?.trace_normalized?.length > 1
                                                            ? journey.trace_normalized
                                                            : journey?.trace
                                                    }
                                                    shape={journey?.shape}
                                                    stops={journey?.stops}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                            {routeHistory.length > ROUTE_HISTORY_PAGE_SIZE && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                                    <Pagination
                                        size='small'
                                        current={routeHistoryCurrent}
                                        pageSize={ROUTE_HISTORY_PAGE_SIZE}
                                        total={routeHistory.length}
                                        showSizeChanger={false}
                                        onChange={(page) => {
                                            setRouteHistoryPage(page)
                                            setExpandedJourney(null)
                                        }}
                                    />
                                </div>
                            )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            <DeviceDrawer
                vehicleDetails={vehicleDetails}
                isVisible={isDrawerVisible}
                onClose={() => setIsDrawerVisible(false)}
                language={language}
                title={`${selectedDevice} Details`}
                devices={devices?.filter(d => d?.vehicle_id === vehicle?.id && d?.device_type === selectedDevice)} />
        </div>
    )
}

export default VehiclesDetails